import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/shared";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Factory, Store, ShieldCheck, TrendingUp, Truck, Wallet, ArrowRight, ChevronDown, BarChart3, Boxes, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const HERO = "https://images.unsplash.com/flagged/photo-1563884542782-13d6a05b17c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBzaG9wa2VlcGVyJTIwc3RvcmV8ZW58MHx8fHwxNzg3OTExMTY4fDA&ixlib=rb-4.1.0&q=85";
const FACTORY = "https://images.unsplash.com/photo-1554070211-e3953a3de374?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwbWFudWZhY3R1cmluZyUyMHdhcmVob3VzZXxlbnwwfHx8fDE3ODc5MTExNjh8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ShieldCheck, t: "Verified network", d: "Every manufacturer & retailer is admin-approved before transacting." },
  { icon: TrendingUp, t: "Transparent margins", d: "Retailers see MRP, price and exact profit margin on every product." },
  { icon: Boxes, t: "Inventory visibility", d: "Live stock, MOQ and dispatch timelines across the catalogue." },
  { icon: BarChart3, t: "Business analytics", d: "Revenue, demand and growth insights for every stakeholder." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-sgrey">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5">
        <Logo />
        <div className="flex items-center gap-2">
          <Button data-testid="nav-pricing" variant="ghost" className="text-forest hover:bg-psage" onClick={() => nav("/pricing")}>Pricing</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="login-dropdown" variant="ghost" className="text-forest hover:bg-psage">
                Login <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="login-manufacturer" onClick={() => nav("/auth?role=manufacturer&mode=login")}><Factory className="mr-2 h-4 w-4" /> Manufacturer Login</DropdownMenuItem>
              <DropdownMenuItem data-testid="login-retailer" onClick={() => nav("/auth?role=retailer&mode=login")}><Store className="mr-2 h-4 w-4" /> Retailer Login</DropdownMenuItem>
              <DropdownMenuItem data-testid="login-admin" onClick={() => nav("/admin/login")}><ShieldCheck className="mr-2 h-4 w-4" /> Admin Login</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-10 lg:grid-cols-2 lg:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="overline inline-flex items-center gap-1.5 rounded-full bg-forest px-3 py-1 text-lime"><Sparkles className="h-3 w-3" /> Smart Commerce Infrastructure</span>
          <h1 className="mt-5 font-heading text-4xl font-semibold tracking-tight text-forest sm:text-5xl lg:text-6xl">
            Connecting Manufacturers With Every Retail Shelf.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-ink/70">
            VIPER OWL is the intelligent commerce infrastructure helping brands expand distribution,
            manage inventory, and connect directly with retailers.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button data-testid="cta-manufacturer" size="lg" className="bg-lime font-semibold text-ink hover:bg-lime/90"
              onClick={() => nav("/auth?role=manufacturer&mode=register")}>
              <Factory className="mr-2 h-5 w-5" /> Register as Manufacturer <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="cta-retailer" size="lg" variant="outline"
              className="border-forest text-forest hover:bg-psage" onClick={() => nav("/auth?role=retailer&mode=register")}>
              <Store className="mr-2 h-5 w-5" /> Join as Retailer
            </Button>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
          <div className="overflow-hidden rounded-xl border border-border shadow-sm">
            <img src={HERO} alt="Retailer" className="h-[380px] w-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-3 hidden w-52 rounded-xl border border-border bg-card p-4 shadow-md sm:block">
            <span className="overline text-olive">Retailer profit margin</span>
            <div className="num text-2xl font-semibold text-forest">₹40 <span className="text-sm text-ink/40">/ unit</span></div>
            <div className="mt-1 h-1.5 w-full rounded-full bg-psage"><div className="h-full w-[40%] rounded-full bg-lime" /></div>
          </div>
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.t} className="rounded-xl border border-border bg-card p-6 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-psage"><f.icon className="h-5 w-5 text-forest" /></div>
              <p className="mt-4 font-heading text-lg font-medium text-forest">{f.t}</p>
              <p className="mt-1 text-sm text-ink/60">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-10">
        <div className="grid overflow-hidden rounded-xl border border-border bg-forest md:grid-cols-2">
          <img src={FACTORY} alt="Factory" className="h-64 w-full object-cover md:h-full" />
          <div className="p-8 lg:p-12">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl">Complete control over your commerce ecosystem.</h2>
            <p className="mt-4 leading-relaxed text-psage">
              Manufacturers reach more retailers faster. Retailers discover profitable products with direct
              ordering. Founders get full visibility across every transaction and every rupee of commission.
            </p>
            <Button data-testid="cta-getstarted" className="mt-6 bg-lime font-semibold text-ink hover:bg-lime/90" onClick={() => nav("/auth?role=manufacturer&mode=register")}>
              Get started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="mx-auto flex max-w-7xl items-center justify-between px-5 py-10 text-sm text-ink/50">
        <Logo />
        <span>© {new Date().getFullYear()} VIPER OWL · Smart Commerce Infrastructure</span>
      </footer>
    </div>
  );
}
